import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "react-bootstrap";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { BASEAPIURL, token } from "../env.js";
import { Button, Chip, Divider, Stack, Typography } from "@mui/material";

const fallbackFacilities = [
  {
    image: "https://via.placeholder.com/300x200",
    title: "Sample Facility",
    nav: "SampleFacility",
    images: ["https://via.placeholder.com/300x200"],
    desc: "This is a fallback facility description.",
    branch: [
      {
        branchName: "Main",
        branchDetails: "Sample details",
        timming: "9AM - 5PM",
        branchPrice: "₹500",
      },
    ],
    key: "fallback-1",
    FacilityGroupID: null,
  },
];

export default function Facility({ showHedeing }) {
  const navigate = useNavigate();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [Allfacilities, setAllfacilities] = useState([]);
  const [carouselImages, setCarouselImages] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  // Optimized shuffle function
  const shuffleArray = useCallback((arr) => {
    const array = [...arr];
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }, []);

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(
          `${BASEAPIURL}/GetFGrpListS?Token=${encodeURIComponent(
            token
          )}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (!res.ok) throw new Error(`HTTP error: ${res.status}`);

        const result = await res.json();
        let facilitiesArray = [];

        if (Array.isArray(result)) {
          facilitiesArray = result;
        } else if (typeof result === "object") {
          const key = Object.keys(result).find((k) => Array.isArray(result[k]));
          facilitiesArray = key ? [...result[key]] : [];
        }

        const transformed = facilitiesArray.map((f, i) => ({
          image: f.FacilityImages?.[0] || "https://via.placeholder.com/300x200",
          title: f.FacilityName,
          nav: f.FacilityName?.replace(/\s+/g, "") || `facility-${i}`,
          images: f.FacilityImages || [],
          desc: f.FacilityDescShort,
          desc2: f.FacilityDescLong,
          branch: f.branches || f.subFacilities || [],
          key: `facility-${f.FacilityGroupID || i}-${i}`,
          FacilityGroupID: f.FacilityGroupID || null,
        }));

        setFacilities(transformed);

        // Limit carousel images to prevent performance issues
        const banners = transformed
          .flatMap((f) => f.images.slice(0, 2))
          .slice(0, 20)
          .map((src, idx) => ({
            src,
            alt: `Carousel image ${idx}`,
            key: `carousel-${idx}`,
          }));

        setCarouselImages(shuffleArray(banners));
      } catch (err) {
        console.error("Fetch failed, using fallback:", err.message);
        setError("Network issue (possibly QUIC error). Showing fallback data.");
        setFacilities(fallbackFacilities);
        setCarouselImages([
          {
            src: fallbackFacilities[0].image,
            alt: fallbackFacilities[0].title,
            key: "fallback-carousel",
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchFacilities();
  }, [shuffleArray]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fixed carousel interval with useRef
  useEffect(() => {
    if (carouselImages.length === 0) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % carouselImages.length);
    }, 3500);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [carouselImages]);

  const handleClick = useCallback(
    (facility) => {
      let nav = facilities?.find(
        (item) => item?.FacilityGroupID == facility?.FacilityGroupID
      )?.nav;

      if (!nav) {
        return;
      }
      navigate(`/Facility/${encodeURIComponent(nav)}`, {
        state: facility,
      });
    },
    [navigate, facilities]
  );

  useEffect(() => {
    const fetchAllFacilities = async () => {
      try {
        setLoading(true);

        const res = await fetch(
          `${BASEAPIURL}/GetAllFGrpList?Token=${encodeURIComponent(
            token
          )}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (!res.ok) throw new Error(`HTTP error: ${res.status}`);

        const result = await res.json();

        setAllfacilities(result?.data);
      } catch (err) {
        console.error("Fetch failed, using fallback:", err.message);
        setError("Network issue (possibly QUIC error). Showing fallback data.");
      } finally {
        setLoading(false);
      }
    };

    fetchAllFacilities();
  }, []);

  // Memoized facility card component
  const FacilityCard = useMemo(
    () =>
      React.memo(({ facility, index, isHovered }) => (
        <div className="col-lg-12 col-md-12 d-flex justify-content-center">
          <Card
            onClick={() => handleClick(facility)}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            style={{
              padding: "5px",
              width: "100%",
              maxWidth: "80vw",
              borderRadius: "12px",
              cursor: "pointer",
              textAlign: "center",
              display: "flex",
              gap: 2,
              flexDirection: "row",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: isHovered ? 0 : "50%",
                width: isHovered ? "100%" : 0,
                height: "4px",
                backgroundColor: "#0d6efd",
                borderRadius: "2px",
                transition: "width 0.3s ease, left 0.3s ease",
              }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignContent: "center",
              }}
            >
              <LazyLoadImage
                src={facility.FacilityImages}
                alt={facility.FacilityGroupName}
                effect="blur"
                style={{
                  width: "150px",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />
              <div
                style={{
                  paddingTop: "10px",
                  fontSize: "1.2em",
                  fontWeight: "600",
                  color: isHovered ? "#0d6efd" : "#b5835c",
                }}
              >
                {facility.FacilityGroupName}
              </div>
            </div>
            <div
              style={{
                padding: "5px",
                fontSize: "18px",
                color: "#000",
                textAlign: "justify",
                lineHeight: "2em",
                marginLeft: "10px",
              }}
            >
              {facility.FacilityDescShort}
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                divider={<Divider orientation="vertical" flexItem />}
                sx={{ mt: 2, flexWrap: "wrap" }}
              >
                {facility?.Facility?.sort((a, b) =>
                  a.FacilityName.localeCompare(b.FacilityName)
                )?.map((item) => {
                  return (
                    <Button
                      variant="contained"
                      onClick={(e) => e?.stopPropagation()}
                      sx={{
                        backgroundColor: "#116ab3ff",
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: "#116ab3ff",
                        },
                      }}
                    >
                      {item?.FacilityName}
                    </Button>
                  );
                })}
              </Stack>
            </div>
            {/* {facility.branch.length > 0 && (
              <div
                style={{
                  padding: "5px",
                  fontSize: "12px",
                  color: "#555",
                  textAlign: "left",
                }}
              >
                <strong>Branches:</strong>
                <ul>
                  {facility.branch.slice(0, 3).map((b, bi) => (
                    <li key={`branch-${facility.key}-${bi}`}>
                      {b.branchName}: {b.branchDetails}
                      {b.timming && ` (${b.timming})`}
                      {b.branchPrice && ` - ${b.branchPrice}`}
                    </li>
                  ))}
                  {facility.branch.length > 3 && (
                    <li>... and {facility.branch.length - 3} more</li>
                  )}
                </ul>
              </div>
            )} */}
          </Card>
        </div>
      )),
    [handleClick]
  );

  // Memoized facility cards rendering
  const facilityCards = useMemo(() => {
    return Allfacilities.map((facility, index) => (
      <FacilityCard
        key={facility.FacilityGroupID}
        facility={facility}
        index={index}
        isHovered={hoveredIndex === index}
      />
    ));
  }, [Allfacilities, hoveredIndex, FacilityCard]);

  // Memoized list items for showHedeing
  // const facilityListItems = useMemo(() => {
  //   return facilities.map((f) => (
  //     <li
  //       className="list-group-item d-flex justify-content-between align-items-start"
  //       key={`list-${f.key}`}
  //     >
  //       <strong>{f.title}</strong>
  //       {f.branch.length > 0 && (
  //         <span>
  //           (
  //           {f.branch
  //             .slice(0, 2)
  //             .map((b) => b.branchName)
  //             .join(", ")}
  //           ){f.branch.length > 2 && ` +${f.branch.length - 2}`}
  //         </span>
  //       )}
  //     </li>
  //   ));
  // }, [facilities]);

  return (
    <div className="container w-100 p-0 m-0">
      {loading && (
        <div className="row p-4 g-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div className="col-md-12 col-sm-12" key={i}>
              <div
                className="card shadow-sm border-0"
                style={{ display: "flex", flexDirection: "row", gap: 1 }}
              >
                {/* 🔹 Image Placeholder */}
                <div className="placeholder-glow">
                  <div
                    className="card-img-top bg-secondary placeholder"
                    style={{ height: "100px", width: "100px" }}
                  ></div>
                </div>

                {/* 🔹 Text Placeholder */}
                <div className="card-body">
                  <h5 className="card-title placeholder-glow mb-3">
                    <span className="placeholder col-6"></span>
                  </h5>
                  <p className="card-text placeholder-glow">
                    <span className="placeholder col-7"></span>
                    <span className="placeholder col-4"></span>
                    <span className="placeholder col-6"></span>
                    <span className="placeholder col-4"></span>
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {error && (
        <div className="text-center my-4 text-danger">
          <h5>Error: {error}</h5>
        </div>
      )}

      {!loading && Allfacilities.length > 0 && (
        <>
          {/* Facility Cards */}
          <div className="row mt-2 mb-5 justify-content-center p-1 g-2">
            {facilityCards}
          </div>

          {/* Optional Heading */}
          {/* {showHedeing && (
            <>
              <div className="row pt-5" />
              <div className="row g-0 d-flex justify-content-center">
                <div className="col-md-8 text-center">
                  <h5 className="title">Facilities</h5>
                  <ul className="list-group list-group-flush">
                    {facilityListItems}
                  </ul>
                </div>
              </div>
            </>
          )} */}
        </>
      )}
    </div>
  );
}
